import React, { useEffect } from "react";
import { Loader2, LogOut, X } from "lucide-react";

export default function ConfirmLogout({
  isOpen,
  onClose,
  onConfirm,
  isLoggingOut = false,
}) {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isLoggingOut) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isLoggingOut, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 px-4 py-6">
      <button
        type="button"
        aria-label="Close logout confirmation"
        className="absolute inset-0 cursor-default"
        onClick={() => {
          if (!isLoggingOut) {
            onClose?.();
          }
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-confirmation-title"
        className="relative z-10 w-full max-w-[420px] rounded-3xl bg-white p-5 shadow-2xl sm:p-6"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isLoggingOut}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={18} />
        </button>

        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <LogOut size={25} />
        </div>

        <h2
          id="logout-confirmation-title"
          className="mt-5 text-xl font-semibold text-gray-800"
        >
          Confirm logout
        </h2>

        <p className="mt-2 text-sm leading-6 text-[#8A9BB5]">
          Are you sure you want to logout from your Chaafo account?
        </p>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoggingOut}
            className="h-11 rounded-full border border-gray-200 px-5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoggingOut}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-red-500 px-5 text-sm font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoggingOut ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <LogOut size={17} />
            )}

            {isLoggingOut ? "Logging out..." : "Logout"}
          </button>
        </div>
      </div>
    </div>
  );
}
